import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAtomValue } from 'jotai';
import '../../../ForListPage.scss';
import { roleMarkerClass } from '../../../../utils/roleMarker';
import '../../../recruit/common/RecruitList.scss';
import './MinisterInfo.scss';
import MainURL from '../../../../MainURL';
import { loginStateAtom, userDataAtom } from '../../../../AtomStore';
import { citydata } from '../../../../DefaultData';
import Pagination from '../../../../components/Pagination';
import Loading from '../../../../components/Loading';
import ScrollToTopButton from '../../../../components/ScrollToTopButton';
import MinisterResumeGuide from './MinisterResumeGuide';
import MinisterResumeSelectModal from './MinisterResumeSelectModal';
import { useMinisterAccess } from './useMinisterAccess';
import type { MinisterInfoListItem } from './ministerInfoTypes';
import { SAMPLE_MINISTER_ID, USE_MINISTER_MOCK, filterMockMinisterList, getSampleMinisterListItem } from './ministerInfoMock';
import { countUpMinisterStat, profileImageSrc } from './ministerInfoUtils';

type FilterTab = '희망유형' | '희망지역' | '성별';

// 상세 페이지 탭과 동일한 키 — /minister/detail?id=..&tab=..
const DOC_LINKS = [
  { tab: 'resume', label: '이력서' },
  { tab: 'intro', label: '자기소개서' },
  { tab: 'portfolio', label: '사역 포트폴리오' },
] as const;

const FILTER_TABS: FilterTab[] = ['희망유형', '희망지역', '성별'];
const HOPE_TYPE_OPTIONS = ['전임', '준전임', '파트'];
const CITY_OPTIONS = citydata.map((city) => city.city);
const GENDER_OPTIONS = ['남', '여'];

interface SearchQuery {
  search: string;
  hopeType: string[];
  city: string[];
  gender: string;
}

const EMPTY_QUERY: SearchQuery = { search: '', hopeType: [], city: [], gender: '' };

// 상단 고정 샘플 — 누구나 상세까지 열람 가능
const SAMPLE_ITEM = getSampleMinisterListItem();

function MinisterInfoListContent() {
  const navigate = useNavigate();
  const userData = useAtomValue(userDataAtom);
  const isLogin = useAtomValue(loginStateAtom);

  // 샘플 외 항목: 로그인만 확인 — 교회 담당자가 아니면 상세에서 모자이크로 보여준다 (서버 /resume/ministerdetail)
  const openDetail = (item: MinisterInfoListItem, path: string) => {
    countUpMinisterStat('ministerinfodetail');
    if (item.id !== SAMPLE_MINISTER_ID) {
      if (!isLogin) {
        if (window.confirm('로그인이 필요합니다.\n로그인 페이지로 이동할까요?')) {
          navigate('/login');
          window.scrollTo(0, 0);
        }
        return;
      }
      // 비공개 이력서는 목록에만 노출 — '지원한 교회만'은 상세에서 서버가 지원 여부를 확인한다
      if (item.visibility === 'PRIVATE') {
        alert('비공개 이력서입니다.');
        return;
      }
    }
    navigate(path);
    window.scrollTo(0, 0);
  };

  const [activeTab, setActiveTab] = useState<FilterTab>('희망유형');
  const [searchWord, setSearchWord] = useState('');
  const [selectedHopeType, setSelectedHopeType] = useState<string[]>([]);
  const [selectedCity, setSelectedCity] = useState<string[]>([]);
  const [selectedGender, setSelectedGender] = useState('');
  // 검색 버튼을 눌렀을 때 확정되는 조회 조건
  const [query, setQuery] = useState<SearchQuery>(EMPTY_QUERY);
  const [currentPage, setCurrentPage] = useState(1);

  const [list, setList] = useState<MinisterInfoListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const [isLoading, setIsLoading] = useState(false);

  // 사용자가 등록한 공개 이력서(서버, 최신순)를 먼저, 목데이터는 그 뒤에 이어서 보여준다
  useEffect(() => {
    let cancelled = false;

    // registeredTitles: 관리자가 DB 에 등록한 목데이터 — 화면 목데이터에서 빼서 중복 노출을 막는다
    const applyPage = (real: MinisterInfoListItem[], realTotal: number, size: number, registeredTitles: string[] = []) => {
      if (cancelled) return;
      const registered = new Set(registeredTitles);
      const mocks = USE_MINISTER_MOCK ? filterMockMinisterList(query).filter((item) => !registered.has(item.title)) : [];
      const offset = (currentPage - 1) * size;
      const mockStart = Math.max(0, offset - realTotal);
      const fill = mocks.slice(mockStart, mockStart + Math.max(0, size - real.length));
      setList([...real, ...fill]);
      setTotalCount(realTotal + mocks.length);
      setPageSize(size);
    };

    setIsLoading(true);
    axios.get(`${MainURL}/resume/ministerlist`, {
      params: {
        userAccount: userData.userAccount,
        page: currentPage,
        search: query.search,
        hopeType: query.hopeType.join(','),
        city: query.city.join(','),
        gender: query.gender,
      },
    })
      .then((res) => {
        if (res.data.success) {
          applyPage(res.data.list || [], res.data.totalCount || 0, res.data.pageSize || 12, res.data.registeredMockTitles || []);
        } else {
          applyPage([], 0, 12);
        }
      })
      .catch((err) => {
        console.error('사역자정보 목록 조회 실패:', err);
        applyPage([], 0, 12);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userData.userAccount, currentPage, query]);

  const toggleItem = (list: string[], item: string) =>
    list.includes(item) ? list.filter((v) => v !== item) : [...list, item];

  const handleSelect = (item: string) => {
    if (activeTab === '희망유형') setSelectedHopeType(toggleItem(selectedHopeType, item));
    if (activeTab === '희망지역') setSelectedCity(toggleItem(selectedCity, item));
    if (activeTab === '성별') setSelectedGender(selectedGender === item ? '' : item);
  };

  const isSelected = (item: string) => {
    if (activeTab === '희망유형') return selectedHopeType.includes(item);
    if (activeTab === '희망지역') return selectedCity.includes(item);
    return selectedGender === item;
  };

  const tabOptions = activeTab === '희망유형' ? HOPE_TYPE_OPTIONS : activeTab === '희망지역' ? CITY_OPTIONS : GENDER_OPTIONS;
  const selectedChips = [...selectedHopeType, ...selectedCity, ...(selectedGender ? [selectedGender] : [])];

  const handleRemoveSelected = (item: string) => {
    setSelectedHopeType(selectedHopeType.filter((v) => v !== item));
    setSelectedCity(selectedCity.filter((v) => v !== item));
    if (selectedGender === item) setSelectedGender('');
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setQuery({ search: searchWord.trim(), hopeType: selectedHopeType, city: selectedCity, gender: selectedGender });
  };

  const handleReset = () => {
    setSearchWord('');
    setSelectedHopeType([]);
    setSelectedCity([]);
    setSelectedGender('');
    setCurrentPage(1);
    setQuery(EMPTY_QUERY);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const renderCard = (item: MinisterInfoListItem) => {
    const isSample = item.id === SAMPLE_MINISTER_ID;
    const latestCareer = item.careerItems[0];
    const latestEducation = item.educationItems[0];
    return (
      <div
        key={item.id}
        className={`minister-info__card${isSample ? ' is-sample' : ''}`}
        onClick={() => openDetail(item, `/minister/detail?id=${item.id}`)}
      >
        <div className="minister-info__card-top">
          {/* 목록 사진은 샘플을 제외하고 모자이크 */}
          <div className={`minister-info__avatar${isSample ? ' is-logo' : ' is-mosaic'}`}>
            {item.profileImage ? (
              <img src={profileImageSrc(item.profileImage)} alt="증명사진" draggable={false} />
            ) : (
              <span>{item.maskedName.slice(0, 1)}</span>
            )}
          </div>
          <div className="minister-info__who">
            <p className="minister-info__name">
              {isSample && <span className="minister-info__sample-badge">샘플</span>}
              {item.maskedName}
            </p>
            <p className="minister-info__meta">
              {[item.gender, item.ageText ? `만 ${item.ageText}세` : '', item.region].filter(Boolean).join(' · ') || '-'}
            </p>
          </div>
          {item.hopeType && <span className={`minister-info__type ${roleMarkerClass(item.hopeType)}`}>{item.hopeType}</span>}
        </div>

        <p className="minister-info__title">{item.title || '제목 없음'}</p>

        <dl className="minister-info__rows">
          <div>
            <dt>경력</dt>
            <dd>
              {latestCareer
                ? [latestCareer.companyName, latestCareer.position].filter(Boolean).join(' · ')
                : '-'}
              {item.careerItems.length > 1 && ` 외 ${item.careerItems.length - 1}건`}
            </dd>
          </div>
          <div>
            <dt>학력</dt>
            <dd>
              {latestEducation
                ? [latestEducation.schoolName, latestEducation.major].filter(Boolean).join(' · ')
                : '-'}
            </dd>
          </div>
          <div>
            <dt>희망</dt>
            <dd>{[item.hopeRoles, item.hopeDepartment].filter(Boolean).join(' / ') || '-'}</dd>
          </div>
          <div>
            <dt>지역</dt>
            <dd>{item.hopeLocation || '-'}</dd>
          </div>
        </dl>

        <div className="minister-info__docs">
          {DOC_LINKS.map(({ tab, label }) => {
            const available =
              tab === 'resume' ||
              (tab === 'intro' && item.hasSelfIntroduction) ||
              (tab === 'portfolio' && (item.portfolioCount ?? 0) > 0);
            return (
              <button
                key={tab}
                type="button"
                className={`minister-info__doc${available ? '' : ' disabled'}`}
                disabled={!available}
                onClick={(e) => {
                  e.stopPropagation();
                  openDetail(item, `/minister/detail?id=${item.id}&tab=${tab}`);
                }}
              >
                {label}
                {tab === 'portfolio' && available && <span>{item.portfolioCount}</span>}
              </button>
            );
          })}
        </div>

        <p className="minister-info__date">수정일 {item.updatedAt}</p>
      </div>
    );
  };

  return (
    <>
      {SAMPLE_ITEM && (
        <section className="minister-info__sample">
          <div className="minister-info__sample-head">
            <h4>샘플 미리보기</h4>
            <p>사역자정보가 어떻게 보이는지 확인할 수 있는 샘플입니다. 눌러서 상세 화면을 확인해보세요.</p>
          </div>
          <div className="minister-info__grid">{renderCard(SAMPLE_ITEM)}</div>
        </section>
      )}

      <div className="subpage__main__tabrow">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab}
            className={`subpage__main__tabbtn${activeTab === tab ? ' active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="checkInputCover Recruit">
        {tabOptions.map((item) => (
          <div
            key={item}
            className={`checkInputbox ${isSelected(item) ? 'selected' : ''}`}
            onClick={() => handleSelect(item)}
          >
            <p>{item}</p>
          </div>
        ))}
      </div>

      <div className="subpage__main__selectedwords">
        <b>선택된 단어:</b>
        <div className="subpage__main__selectedwords__list">
          {selectedChips.length === 0 ? (
            <span className="subpage__main__selectedwords__none">없음</span>
          ) : (
            selectedChips.map((item) => (
              <span key={item} className="subpage__main__selectedwords__item">
                {item}
                <button
                  className="subpage__main__selectedwords__remove"
                  onClick={() => handleRemoveSelected(item)}
                  aria-label="삭제"
                  type="button"
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>
      </div>

      <div className="subpage__main__search">
        <input
          className="inputdefault width"
          type="text"
          placeholder="희망 직무, 부서, 경력, 학교로 검색"
          value={searchWord}
          onChange={(e) => setSearchWord(e.target.value)}
          style={{ margin: '0', outline: 'none', paddingLeft: '10px', border: '1px solid #ccc' }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
        />
        <div className="buttons">
          <div className="btn search" onClick={handleSearch}>
            <p>검색</p>
          </div>
          <div className="btn reset" onClick={handleReset}>
            <p>초기화</p>
          </div>
        </div>
      </div>

      <div className="subpage__main__content">
        <p className="minister-info__count">총 <b>{totalCount}</b>명</p>
        {isLoading ? (
          <div className="list-loading">
            <Loading />
          </div>
        ) : list.length === 0 ? (
          <div className="minister-info__empty">
            <p>조건에 맞는 사역자가 없습니다.</p>
          </div>
        ) : (
          <div className="minister-info__grid">
            {list.map(renderCard)}
          </div>
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          onAfterChange={() => window.scrollTo(0, 500)}
        />
      </div>
    </>
  );
}

export default function MinisterInfoList() {
  const navigate = useNavigate();
  const isLogin = useAtomValue(loginStateAtom);
  const access = useMinisterAccess();
  const [isResumeSelectOpen, setIsResumeSelectOpen] = useState(false);

  useEffect(() => {
    countUpMinisterStat('ministerinfoview');
  }, []);

  const openMyInfo = () => {
    if (!isLogin) {
      if (window.confirm('로그인이 필요합니다.\n로그인 페이지로 이동할까요?')) {
        navigate('/login');
        window.scrollTo(0, 0);
      }
      return;
    }
    if (access.loading) return;
    if (access.resumeList.length === 0) {
      if (window.confirm('아직 등록된 이력서가 없습니다.\n이력서를 작성하러 이동할까요?')) {
        navigate('/mypage/resumeedit');
        window.scrollTo(0, 0);
      }
      return;
    }
    // 이력서가 2개 이상이면 확인할 이력서를 먼저 선택
    if (access.resumeList.length > 1) {
      setIsResumeSelectOpen(true);
      return;
    }
    goMyInfo(String(access.resumeList[0].id));
  };

  const goMyInfo = (resumeId: string) => {
    setIsResumeSelectOpen(false);
    navigate(`/minister/my?id=${resumeId}`);
    window.scrollTo(0, 0);
  };

  return (
    <div className="recruit minister-info">
      <div className="inner">
        <div className="subpage__main">
          <div className="subpage__main__title">
            <h3>사역자정보</h3>
            <button type="button" className="minister-info__my-btn" onClick={openMyInfo}>
              내 정보 보기
            </button>
          </div>
          {access.userType === 'MINISTER' && !access.loading && access.blockReason !== 'login' && (
            <MinisterResumeGuide resumeList={access.resumeList} />
          )}
          <MinisterInfoListContent />
        </div>
      </div>
      <ScrollToTopButton />
      {isResumeSelectOpen && (
        <MinisterResumeSelectModal
          resumeList={access.resumeList}
          onClose={() => setIsResumeSelectOpen(false)}
          onSelect={goMyInfo}
        />
      )}
    </div>
  );
}
